import { useMemo, useState } from 'react'
import {
    Alert,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    Checkbox,
    FormControlLabel,
} from '@mui/material'
import DetailsImg from '@/assets/img/realtime/details.png'
import { DescriptionComponent, TitleComponent } from '@/components/Basic/library'
import useAuth from '@/context/useAuth'
import { changeComfortMode } from '@/services/constraints.service'
import { useSite } from '../../useSite'
import { useTranslation } from 'react-i18next'

const buildEndDate = (days) => {
    const date = new Date()
    date.setDate(date.getDate() + days)
    date.setHours(0, 0, 0, 0)
    return date
}

export const ComfortModeControl = () => {
    const { site } = useSite()
    const { me } = useAuth()
    const { t } = useTranslation('site', { keyPrefix: 'constraints.comfortMode' })
    const [open, setOpen] = useState(false)
    const [mode, setMode] = useState('comfort')
    const [duration, setDuration] = useState(7)
    const [saving, setSaving] = useState(false)
    const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' })
    const [acknowledged, setAcknowledged] = useState(false)

    const isAgridUser = me?.email?.includes('@a-grid.com')

    const durations = useMemo(
        () => [
            { value: 7, label: t('durations.oneWeek') },
            { value: 14, label: t('durations.twoWeeks') },
            { value: 30, label: t('durations.oneMonth') },
            { value: 90, label: t('durations.threeMonths') },
            { value: 'none', label: t('durations.noEndDate') },
        ],
        [t]
    )

    // if (!isAgridUser) return null

    const handleDurationChange = (event) => {
        const value = event.target.value
        if (value === 'none') setDuration(null)
        else setDuration(Number(value))
    }

    const handleClose = () => {
        setOpen(false)
        setAcknowledged(false)
    }

    const handleSubmit = async () => {
        if (!site?.hash || saving) return
        setSaving(true)
        try {
            const endDate = mode === 'savings' ? null : duration === null ? null : buildEndDate(duration)
            const res = await changeComfortMode({ target: mode, endDate }, site.hash)
            const isSuccess = res?.success !== false && !res?.crash
            setSnackbar({
                open: true,
                severity: isSuccess ? 'success' : 'error',
                message: isSuccess ? t('status.success') : t('status.error'),
            })
            if (isSuccess) setOpen(false)
        } catch (error) {
            setSnackbar({ open: true, severity: 'error', message: t('status.error') })
        } finally {
            setSaving(false)
        }
    }

    const durationValue = duration === null ? 'none' : duration

    const currentMode = site?.comfortStatus || 'savings'
    const comfortEndDate = site?.comfortEndDate

    const getChipProps = () => {
        if (currentMode === 'comfort') {
            return {
                label: t('modes.comfort'),
                color: 'warning',
            }
        }
        return {
            label: t('modes.savings'),
            color: 'success',
        }
    }

    return (
        <Grid item md={12} sm={12} xs={12}>
            <Card style={{ marginBottom: 40 }}>
                <CardContent>
                    <div
                        style={{
                            color: '#050C22',
                            fontSize: 14,
                            display: 'flex',
                            alignItems: 'center',
                            fontWeight: 500,
                            justifyContent: 'space-between',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img src={DetailsImg} style={{ marginRight: 10, maxHeight: 20 }} />
                            <TitleComponent title={t('title')} style={{ fontSize: 15 }} />
                        </div>
                        <Chip {...getChipProps()} size="small" />
                    </div>
                    {/* <div style={{ marginTop: 10 }}>
                        <DescriptionComponent content={t('description')} />
                    </div> */}
                    {currentMode === 'comfort' && comfortEndDate && (
                        <Typography variant="caption" style={{ display: 'block', marginTop: 8, color: '#6B7280' }}>
                            {t('status.activeUntil', {
                                date: new Date(comfortEndDate).toLocaleDateString(),
                            })}
                        </Typography>
                    )}
                    <div style={{ marginTop: 12 }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setAcknowledged(false)
                                setOpen(true)
                            }}
                        >
                            {t('button')}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{t('dialogTitle')}</DialogTitle>
                <DialogContent>
                    {/* <DialogContentText>{t('description')}</DialogContentText> */}
                    <div style={{ marginTop: 16 }}>
                        <Typography variant="body2" style={{ marginBottom: 6 }}>
                            {t('fields.mode')}
                        </Typography>
                        <ToggleButtonGroup
                            value={mode}
                            exclusive
                            onChange={(event, newValue) => {
                                if (newValue !== null) setMode(newValue)
                            }}
                            color="primary"
                            size="small"
                        >
                            <ToggleButton value="comfort">{t('modes.comfort')}</ToggleButton>
                            <ToggleButton value="savings">{t('modes.savings')}</ToggleButton>
                        </ToggleButtonGroup>
                    </div>
                    {mode === 'comfort' && (
                        <>
                            <Typography
                                variant="caption"
                                component="div"
                                style={{ marginTop: 16, color: '#6B7280', fontSize: 12 }}
                            >
                                {t('modeDescription.intro')}
                                <ul style={{ margin: '6px 0 0 0', paddingLeft: 18 }}>
                                    <li>{t('modeDescription.bullet1')}</li>
                                    <li>{t('modeDescription.bullet2')}</li>
                                </ul>
                                <span style={{ display: 'block', marginTop: 6 }}>{t('modeDescription.useCases')}</span>
                            </Typography>
                            <div style={{ marginTop: 20 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="comfort-mode-duration-label">{t('fields.duration')}</InputLabel>
                                    <Select
                                        labelId="comfort-mode-duration-label"
                                        id="comfort-mode-duration"
                                        value={durationValue}
                                        label={t('fields.duration')}
                                        onChange={handleDurationChange}
                                    >
                                        {durations.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Typography
                                    variant="caption"
                                    style={{ display: 'block', marginTop: 6, color: '#6B7280' }}
                                >
                                    {t('helper.duration')}
                                </Typography>
                            </div>
                            <div style={{ marginTop: 20 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={acknowledged}
                                            onChange={(event) => setAcknowledged(event.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={
                                        <Typography variant="caption" style={{ fontSize: 11 }}>
                                            {t('fields.acknowledgement')}
                                        </Typography>
                                    }
                                />
                                <Alert severity="info" style={{ marginTop: 12 }}>
                                    {t('fields.infoAlert')}
                                </Alert>
                            </div>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="inherit" disabled={saving}>
                        {t('actions.cancel')}
                    </Button>
                    <Button onClick={handleSubmit} disabled={saving || (mode === 'comfort' && !acknowledged)}>
                        {t('actions.apply')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Grid>
    )
}
